import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Badge, ErrorBox } from '../components/common';
import { formatDate } from '../labels';

// Vérification d'identité par NIP (facultative) : jamais un rattachement au registre ni une délivrance.

type CitizenStatus = 'VERIFIED' | 'DIVERGENCE' | 'NO_MATCH' | 'AMBIGUOUS' | 'UNAVAILABLE' | 'MANUAL_REVIEW';

interface NipAttributes {
  surname: string | null;
  givenNames: string | null;
  birthYear: number | null;
  birthMonth: number | null;
  birthDay: number | null;
  birthPlace: string | null;
}

interface NipResult {
  status: CitizenStatus;
  message: string;
  attributes: NipAttributes | null;
  provenance: string | null;
  checkedAt: string | null;
  canConfirm: boolean;
}

interface NipSummary {
  enabled: boolean;
  verified: boolean;
  confirmedAt: string | null;
  attemptsLeft: number;
  last: NipResult | null;
  agent?: {
    status: string | null;
    provider: string | null;
    providerReference: string | null;
    checkedAt: string | null;
    divergences: string[];
    attempts: number;
    reviewReason: string | null;
    reviewedAt: string | null;
    reviewedBy: string | null;
    reviewNote: string | null;
  };
}

const AGENT_STATUS: Record<string, { label: string; tone: string }> = {
  VERIFIED: { label: 'Vérifiée', tone: 'ok' },
  NO_MATCH: { label: 'Introuvable', tone: 'warn' },
  AMBIGUOUS: { label: 'Ambiguë', tone: 'warn' },
  UNAVAILABLE: { label: 'Service indisponible', tone: 'muted' },
  MANUAL_REVIEW: { label: 'Revue manuelle demandée', tone: 'warn' },
};

export const REVIEW_REASON: Record<string, string> = {
  introuvable: 'NIP introuvable',
  ambigu: 'Réponse ambiguë',
  indisponible: 'Service indisponible',
  revue_manuelle: 'Revue manuelle demandée',
  divergence: 'Saisie différente du référentiel',
};

const FIELD_LABEL: Record<string, string> = {
  surname: 'Nom',
  givenNames: 'Prénoms',
  birthYear: 'Année de naissance',
  birthMonth: 'Mois de naissance',
  birthDay: 'Jour de naissance',
  birthPlace: 'Lieu de naissance',
};

function birth(a: NipAttributes) {
  const date = [a.birthDay, a.birthMonth, a.birthYear]
    .map((v) => (v === null ? null : String(v).padStart(2, '0')))
    .filter(Boolean)
    .join('/');
  return [date, a.birthPlace].filter(Boolean).join(', ') || 'non communiquée';
}

/** Carte de la demande : vérification par l'usager (brouillon) ou lecture par l'agent d'instruction. */
export function NipCard({
  applicationId,
  status,
  role,
  autoOpen,
  onChange,
}: {
  applicationId: string;
  status: string;
  role: string;
  autoOpen?: boolean;
  onChange: () => void;
}) {
  const [summary, setSummary] = useState<NipSummary | null>(null);
  const load = useCallback(
    () =>
      api<NipSummary>(`/demandes/${applicationId}/nip`)
        .then(setSummary)
        .catch(() => setSummary(null)),
    [applicationId],
  );
  useEffect(() => {
    void load();
  }, [load]);
  if (!summary?.enabled) return null;
  if (summary.agent) return <AgentView id={applicationId} s={summary} onDone={setSummary} />;
  if (role !== 'owner' && role !== 'counter') return null;
  if (status !== 'brouillon') {
    return summary.confirmedAt ? (
      <section className="card flow">
        <h2>Vérification NIP</h2>
        <p className="alert alert-ok">Identité vérifiée par NIP le {formatDate(summary.confirmedAt)}.</p>
      </section>
    ) : null;
  }
  return (
    <CitizenView
      id={applicationId}
      s={summary}
      autoOpen={autoOpen}
      onDone={() => {
        void load();
        onChange();
      }}
    />
  );
}

function CitizenView({
  id,
  s,
  autoOpen,
  onDone,
}: {
  id: string;
  s: NipSummary;
  autoOpen?: boolean;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(!!autoOpen);
  const [declined, setDeclined] = useState(false);
  const [nip, setNip] = useState('');
  const [consent, setConsent] = useState(false);
  const [result, setResult] = useState<NipResult | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  if (s.confirmedAt)
    return (
      <section className="card flow">
        <h2>Vérification NIP</h2>
        <p className="alert alert-ok">
          Identité vérifiée par NIP et confirmée le {formatDate(s.confirmedAt)}. Elle a complété votre
          identité déclarée.
        </p>
      </section>
    );

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<NipResult>(`/demandes/${id}/nip/verifier`, {
        method: 'POST',
        body: { nip, idempotencyKey: crypto.randomUUID(), consent },
      });
      setResult(r);
      setNip('');
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/demandes/${id}/nip/confirmer`, { method: 'POST' });
      onDone();
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  };
  const shown = result ?? s.last;

  return (
    <section className="card flow nip-card" aria-labelledby="nip-h">
      <h2 id="nip-h">Vérifier mon identité avec mon NIP</h2>
      {declined ? (
        <div className="ch">
          <span className="muted">Vous continuez sans NIP : un agent vérifiera votre identité.</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeclined(false)}>
            Utiliser mon NIP
          </button>
        </div>
      ) : !open && !shown ? (
        <>
          <p className="small muted" style={{ margin: 0 }}>
            Facultatif. Votre numéro d’identification personnel peut confirmer votre identité et compléter
            votre saisie. Sans NIP, votre demande suit exactement le même circuit.
          </p>
          <div className="actions">
            <button type="button" className="btn" onClick={() => setOpen(true)}>
              Vérifier mon identité avec mon NIP
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setDeclined(true)}>
              Continuer sans NIP
            </button>
          </div>
        </>
      ) : (
        <>
          {shown && (
            <div className={`alert ${shown.status === 'VERIFIED' ? 'alert-ok' : 'alert-info'}`} role="status">
              {shown.message}
            </div>
          )}
          {result?.attributes && result.canConfirm && (
            <div className="card tint flow nip-result">
              <dl className="details">
                <dt>Nom</dt>
                <dd>{result.attributes.surname ?? 'non communiqué'}</dd>
                <dt>Prénoms</dt>
                <dd>{result.attributes.givenNames ?? 'non communiqués'}</dd>
                <dt>Naissance</dt>
                <dd>{birth(result.attributes)}</dd>
                <dt>Provenance</dt>
                <dd className="small">
                  {result.provenance}
                  {result.checkedAt ? `, ${formatDate(result.checkedAt)}` : ''}
                </dd>
              </dl>
              <div className="actions">
                <button type="button" className="btn" disabled={busy} onClick={() => void confirm()}>
                  Confirmer ces informations
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setResult(null)}>
                  Ce ne sont pas mes informations
                </button>
              </div>
            </div>
          )}
          {s.attemptsLeft > 0 && !(result?.canConfirm && result.attributes) && (
            <form className="nip-form" onSubmit={verify}>
              <p className="small muted" style={{ margin: 0 }}>
                Votre NIP sert uniquement à vérifier votre identité pour cette demande. Il est transmis de
                façon sécurisée au service d’identification, n’est ni conservé ni affiché, et seul le résultat
                de la vérification est enregistré. Saisissez d’abord votre nom et votre année de naissance
                dans la demande.
              </p>
              <div className="fg">
                <div className="field">
                  <label htmlFor="nip">NIP</label>
                  <input
                    id="nip"
                    name="nip"
                    autoComplete="off"
                    inputMode="text"
                    required
                    minLength={4}
                    maxLength={40}
                    value={nip}
                    onChange={(e) => setNip(e.target.value)}
                  />
                  <span className="hint">
                    {s.attemptsLeft} essai{s.attemptsLeft > 1 ? 's' : ''} restant
                    {s.attemptsLeft > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              <label className="checkbox">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span>J’ai pris connaissance de l’usage de mon NIP décrit ci-dessus.</span>
              </label>
              <ErrorBox error={error} />
              <div className="actions">
                <button className="btn" type="submit" disabled={busy || !consent || nip.trim().length < 4}>
                  {busy ? 'Vérification…' : 'Vérifier'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setOpen(false);
                    setResult(null);
                    setDeclined(true);
                  }}
                >
                  Continuer sans NIP
                </button>
              </div>
            </form>
          )}
          {result?.canConfirm && <ErrorBox error={error} />}
        </>
      )}
    </section>
  );
}

function AgentView({ id, s, onDone }: { id: string; s: NipSummary; onDone: (s: NipSummary) => void }) {
  const a = s.agent!;
  const [note, setNote] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  if (!a.status) return null;
  const st =
    a.status === 'VERIFIED' && a.divergences.length
      ? { label: 'Saisie différente', tone: 'warn' }
      : (AGENT_STATUS[a.status] ?? { label: a.status, tone: 'muted' });
  const review = async () => {
    setError(null);
    try {
      onDone(await api<NipSummary>(`/demandes/${id}/nip/revue`, { method: 'POST', body: { note } }));
    } catch (e) {
      setError(e as ApiError);
    }
  };
  return (
    <section className="card flow" aria-labelledby="nip-a-h">
      <h2 id="nip-a-h">Vérification NIP</h2>
      <dl className="details">
        <dt>Résultat</dt>
        <dd>
          <Badge tone={st.tone}>{st.label}</Badge>
          {s.confirmedAt && <span className="small muted"> - confirmé par l’usager</span>}
        </dd>
        <dt>Source</dt>
        <dd>
          {a.provider === 'MockNip' || a.provider === 'NipOfficiel' ? 'Référentiel NIP' : a.provider}
          {a.checkedAt ? `, ${formatDate(a.checkedAt)}` : ''}
          {a.providerReference && (
            <div className="small mono" style={{ overflowWrap: 'anywhere' }}>
              {a.providerReference}
            </div>
          )}
        </dd>
        {a.divergences.length > 0 && (
          <>
            <dt>Différences</dt>
            <dd>{a.divergences.map((f) => FIELD_LABEL[f] ?? f).join(', ')}</dd>
          </>
        )}
        <dt>Essais</dt>
        <dd>{a.attempts}</dd>
      </dl>
      <p className="small muted" style={{ margin: 0 }}>
        Indication d’identification seulement : elle ne rattache pas la demande au registre et n’autorise
        aucune délivrance.
      </p>
      {a.reviewReason &&
        (a.reviewedAt ? (
          <p className="alert alert-ok">
            Revue faite par {a.reviewedBy} le {formatDate(a.reviewedAt)} : {a.reviewNote}
          </p>
        ) : (
          <div className="card tint flow">
            <p style={{ margin: 0 }}>
              <strong>Identité à revoir</strong> - {REVIEW_REASON[a.reviewReason] ?? a.reviewReason}. Vérifiez
              l’identité sur les pièces jointes, sans conclure à une fraude.
            </p>
            <div className="field">
              <label htmlFor="nip-note">Comment l’identité a été vérifiée</label>
              <input id="nip-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <ErrorBox error={error} />
            <div className="actions">
              <button
                type="button"
                className="btn"
                disabled={note.trim().length < 5}
                onClick={() => void review()}
              >
                Identité vérifiée manuellement
              </button>
            </div>
          </div>
        ))}
    </section>
  );
}

/** File des demandes dont l'identité est à revoir (espace Délivrance). */
export function NipReviewQueue() {
  const [rows, setRows] = useState<
    {
      id: string;
      reference: string;
      status: string;
      submitted_at: string | null;
      nip_review_reason: string;
    }[]
  >([]);
  useEffect(() => {
    void api<typeof rows>('/verification-nip/revue')
      .then(setRows)
      .catch(() => setRows([]));
  }, []);
  if (!rows.length) return null;
  return (
    <section className="card np" aria-labelledby="nip-q-h">
      <div className="pl-card-h">
        <h2 id="nip-q-h">Identité à revoir (NIP)</h2>
      </div>
      <div className="table-wrap">
        <table aria-labelledby="nip-q-h">
          <thead>
            <tr>
              <th scope="col">Demande</th>
              <th scope="col">Motif</th>
              <th scope="col">Déposée</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <Link to={`/espace/demandes/${r.id}`} className="mono">
                    {r.reference}
                  </Link>
                </td>
                <td data-l="Motif">{REVIEW_REASON[r.nip_review_reason] ?? r.nip_review_reason}</td>
                <td data-l="Déposée">{r.submitted_at ? formatDate(r.submitted_at) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
